import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import axios from 'axios'

const SLICE_NAME = 'proCons'

function prepConfig(getState: any) {
	const {
		userLogin: { userInfo },
	} = getState()
	return { headers: { Authorization: `Bearer ${userInfo.token}` } }
}

const initialState = {
	bySite: {} as Record<string, any[]>,
	comparisonByEvaluation: {} as Record<string, any[]>,
	isLoading: false,
	error: null as string | null,
}

export const fetchSiteProsCons = createAsyncThunk(
	`${SLICE_NAME}/fetch`,
	async (
		{ evalId, siteId }: { evalId: string; siteId: string },
		{ getState, rejectWithValue },
	) => {
		try {
			const { data } = await axios.get(
				`/api/evaluations/${evalId}/sites/${siteId}/proscons`,
				prepConfig(getState),
			)
			return { siteId, items: data }
		} catch {
			return rejectWithValue('Failed to load pros/cons')
		}
	},
)

export const createProCon = createAsyncThunk(
	`${SLICE_NAME}/create`,
	async (
		{ evalId, siteId, payload }: { evalId: string; siteId: string; payload: any },
		{ getState, rejectWithValue },
	) => {
		try {
			const { data } = await axios.post(
				`/api/evaluations/${evalId}/sites/${siteId}/proscons`,
				payload,
				prepConfig(getState),
			)
			return { siteId, item: data }
		} catch {
			return rejectWithValue('Failed to create pro/con')
		}
	},
)

export const updateProCon = createAsyncThunk(
	`${SLICE_NAME}/update`,
	async (
		{
			evalId,
			siteId,
			itemId,
			payload,
		}: { evalId: string; siteId: string; itemId: string; payload: any },
		{ getState, rejectWithValue },
	) => {
		try {
			const { data } = await axios.put(
				`/api/evaluations/${evalId}/sites/${siteId}/proscons/${itemId}`,
				payload,
				prepConfig(getState),
			)
			return { siteId, item: data }
		} catch {
			return rejectWithValue('Failed to update pro/con')
		}
	},
)

export const deleteProCon = createAsyncThunk(
	`${SLICE_NAME}/delete`,
	async (
		{ evalId, siteId, itemId }: { evalId: string; siteId: string; itemId: string },
		{ getState, rejectWithValue },
	) => {
		try {
			await axios.delete(
				`/api/evaluations/${evalId}/sites/${siteId}/proscons/${itemId}`,
				prepConfig(getState),
			)
			return { siteId, itemId }
		} catch {
			return rejectWithValue('Failed to delete pro/con')
		}
	},
)

export const voteProCon = createAsyncThunk(
	`${SLICE_NAME}/vote`,
	async (
		{ evalId, siteId, itemId }: { evalId: string; siteId: string; itemId: string },
		{ getState, rejectWithValue },
	) => {
		try {
			const { data } = await axios.post(
				`/api/evaluations/${evalId}/sites/${siteId}/proscons/${itemId}/vote`,
				{},
				prepConfig(getState),
			)
			return { siteId, item: data }
		} catch {
			return rejectWithValue('Failed to vote')
		}
	},
)

export const fetchProsConsComparison = createAsyncThunk(
	`${SLICE_NAME}/fetchCompare`,
	async (evalId: string, { getState, rejectWithValue }) => {
		try {
			const { data } = await axios.get(
				`/api/evaluations/${evalId}/proscons/compare`,
				prepConfig(getState),
			)
			return { evalId, comparison: data }
		} catch {
			return rejectWithValue('Failed to load pros/cons comparison')
		}
	},
)

// ============================================================================
// Apply a pros/cons template across the evaluation's sites.
//
// Mirrors POST /api/evaluations/:evalId/apply-pros-cons-template. The shared
// ApplyTemplateModalCN calls this URL directly via axios — this thunk is a
// Redux-shaped affordance for callers that want dispatch visibility.
// Callers should re-fetch per-site pros/cons + the comparison after apply
// to refresh the columns and summary.
// ============================================================================
export const applyProsConsTemplate = createAsyncThunk(
	`${SLICE_NAME}/applyTemplate`,
	async (
		payload: {
			evalId: string
			templateId: string
			overwrite?: boolean
			applyTo?: 'all' | string
		},
		{ getState, rejectWithValue },
	) => {
		try {
			const { evalId, templateId, overwrite, applyTo } = payload
			const { data } = await axios.post(
				`/api/evaluations/${evalId}/apply-pros-cons-template`,
				{
					templateId,
					overwrite: !!overwrite,
					applyTo: applyTo || 'all',
				},
				prepConfig(getState),
			)
			return data
		} catch (err: any) {
			return rejectWithValue(
				err?.response?.data?.message || 'Failed to apply pros/cons template',
			)
		}
	},
)

const proConSlice = createSlice({
	name: SLICE_NAME,
	initialState,
	reducers: {},
	extraReducers: (builder) => {
		builder
			.addCase(fetchSiteProsCons.fulfilled, (state, action) => {
				const { siteId, items } = action.payload
				state.bySite[siteId] = items
			})
			.addCase(createProCon.fulfilled, (state, action) => {
				const { siteId, item } = action.payload
				if (!state.bySite[siteId]) state.bySite[siteId] = []
				state.bySite[siteId].unshift(item)
			})
			.addCase(updateProCon.fulfilled, (state, action) => {
				const { siteId, item } = action.payload
				const list = state.bySite[siteId] || []
				const idx = list.findIndex((i) => i._id === item._id)
				if (idx >= 0) list[idx] = item
			})
			.addCase(voteProCon.fulfilled, (state, action) => {
				const { siteId, item } = action.payload
				const list = state.bySite[siteId] || []
				const idx = list.findIndex((i) => i._id === item._id)
				if (idx >= 0) list[idx] = item
			})
			.addCase(deleteProCon.fulfilled, (state, action) => {
				const { siteId, itemId } = action.payload
				if (state.bySite[siteId]) {
					state.bySite[siteId] = state.bySite[siteId].filter((i) => i._id !== itemId)
				}
			})
			.addCase(fetchProsConsComparison.fulfilled, (state, action) => {
				const { evalId, comparison } = action.payload
				state.comparisonByEvaluation[evalId] = comparison
			})
	},
})

export default proConSlice
